import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsCategories, newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import CardSkeleton from "@/components/feature/CardSkeleton";
import usePageMeta from "@/hooks/usePageMeta";

export default function NewsPage() {
  const [category, setCategory] = useState<string>("全部");
  const [sort, setSort] = useState<"date" | "hot">("date");
  const [loading, setLoading] = useState(true);

  usePageMeta(
    "影视资讯专题 - 善良的秘书",
    "善良的秘书影视资讯专题，汇集影视新闻、深度剧评与专题分析，按时间或热度浏览最新影视动态。"
  );

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [category, sort]);

  const list = useMemo(() => {
    let arr = newsList.filter((n) => category === "全部" || n.category === category);
    arr = [...arr].sort((a, b) => {
      if (sort === "hot") return b.title.length - a.title.length;
      return a.date < b.date ? 1 : -1;
    });
    return arr;
  }, [category, sort]);

  const tabs = ["全部", ...newsCategories];

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">影视资讯专题</h1>
        <p className="mt-2 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
          追踪荧屏内外的热门话题：新剧前瞻、深度剧评、专题分析与行业观察，帮你在开播前就做好功课。
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:flex-row md:items-center md:justify-between md:p-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setCategory(t)}
              className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                category === t
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500">排序</span>
          <div className="flex rounded-full border border-background-200 p-1">
            <button
              type="button"
              onClick={() => setSort("date")}
              className={`rounded-full px-3 py-1 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                sort === "date" ? "bg-primary-100 text-primary-700" : "text-foreground-600"
              }`}
            >
              最新
            </button>
            <button
              type="button"
              onClick={() => setSort("hot")}
              className={`rounded-full px-3 py-1 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                sort === "hot" ? "bg-primary-100 text-primary-700" : "text-foreground-600"
              }`}
            >
              热门
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <div className="h-32 w-full sm:w-56 shrink-0 animate-pulse rounded-md bg-background-200"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-background-200"></div>
                    <div className="h-3 w-full animate-pulse rounded bg-background-200"></div>
                    <div className="h-3 w-2/3 animate-pulse rounded bg-background-200"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : list.length > 0 ? (
            <div className="space-y-4">
              {list.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex flex-col sm:flex-row gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-300 hover:-translate-y-0.5"
                >
                  <div className="h-44 w-full sm:h-32 sm:w-56 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={n.thumbnail}
                      alt={`${n.title} 配图`}
                      title={`${n.title} 影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-md bg-accent-100 px-2 py-0.5 text-accent-900">{n.category}</span>
                      <span className="font-label text-foreground-500">{n.date}</span>
                    </div>
                    <h2 className="mt-2 font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {n.title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{n.summary}</p>
                    <span className="mt-auto pt-3 flex items-center gap-1 text-sm text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-newspaper-line"
              title="暂无相关资讯"
              description="这个分类下暂时还没有内容，切换其他分类看看。"
            />
          )}
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-bar-chart-2-line text-accent-600"></i>
              热门资讯
            </h2>
            <ol className="mt-4 space-y-3">
              {[...newsList]
                .sort((a, b) => (a.date < b.date ? 1 : -1))
                .slice(0, 5)
                .map((n, i) => (
                  <li key={n.id}>
                    <Link to={`/news/${n.id}`} className="group flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-bold bg-primary-500 text-background-50">
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {n.title}
                      </span>
                    </Link>
                  </li>
                ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-900 p-5 text-primary-100">
            <h2 className="font-heading text-base font-bold text-background-50">想找剧集？</h2>
            <p className="mt-2 text-sm leading-relaxed text-primary-200">
              资讯看完了，直接去分类页按类型和年份筛选，找到下一部想追的剧。
            </p>
            <Link
              to="/category"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-primary-950 whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
            >
              <i className="ri-list-check-2"></i>
              浏览剧集分类
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}